import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, categoriesMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'character-background');
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  useEffect(() => {
    setArticles(getArticlesByCategory(activeTab));
  }, [activeTab]);

  const currentCategory = categoriesMap[activeTab];
  const categoryName = currentCategory?.name || '分类';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 拳皇档案馆`,
    description: currentCategory?.description || '',
    url: `https://maishiranui-guide.com/category/${activeTab}`,
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://maishiranui-guide.com/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SchemaOrg data={pageSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />

          <section className="w-full bg-background-50 py-10 md:py-16">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-10">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-600">
                  {currentCategory?.description}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveTab(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-200/70 text-foreground-600 hover:bg-background-300/70'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {articles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}